import * as d3 from 'd3';
import { getRandomInt32 } from '../../../../common/helpers/getRandom';
import { CategoricalChartData } from './CategoricalChart.model';

export class XAxis {
    private _xScale: d3.scale.Ordinal<any, any>;
    private _xAxis: d3.svg.Axis;
    private _xAxisGroup: d3.Selection<any>;
    private _padding: number = 0.5;
    private _width: number;
    private _height: number;
    private _clipPathId: string;

    constructor(container: d3.Selection<SVGGElement>, width: number, height: number) {
        this._clipPathId = `labelsClipPath${getRandomInt32()}`;
        this.init(container, width, height, this._clipPathId);
        this.width(width)
            .height(height);
    }

    private init(container: d3.Selection<SVGGElement>, width: number, height: number, clipPathId: string) {
        const xScale = d3.scale.ordinal();
        // .rangeBands([0, width], this._padding);
        const xAxis = d3.svg.axis()
            .tickFormat(d => {
                return d.label;
            })
            .scale(xScale);
        const clippath = container.append('clipPath')
            .attr('id', clipPathId);
        const xAxisGroup = container.append('g')
            .classed('axis', true)
            .classed('x', true);
        clippath.append('rect')
            //.attr('x', -band / 2)
            // .attr('width', band)
            .attr('height', 20);
        this._xScale = xScale;
        this._xAxis = xAxis;
        this._xAxisGroup = xAxisGroup;
    }

    spacing(): number;
    spacing(value: number): this;
    spacing(value?: number): this | number {
        if (!arguments.length) {
            return this._padding;
        }
        this._padding = value;
        return this;
    }

    height(): number;
    height(value: number): this;
    height(value?: number): this | number {
        if (!arguments.length) {
            return this._height;
        }
        this._height = value;
        return this;
    }

    width(): number;
    width(value: number): this;
    width(value?: number): this | number {
        if (!arguments.length) {
            return this._width;
        }
        this._width = value;
        return this;
    }

    position(value: CategoricalChartData): number {
        return this._xScale(value.id);
    }

    index(position: number): number {
        const bandWidth = this._xScale.rangeBand();
        const ranges = this._xScale.range()
            .map(d => { return [d - bandWidth / 2, d + 3 * bandWidth / 2]; });
        const matching = ranges.filter(r => r[0] <= position && r[1] > position);
        let idx = 0;
        if (matching.length) {
            idx = ranges.indexOf(matching[0]);
        } else {
            if (d3.max(ranges, m => m[1]) < position) {
                idx = ranges.length - 1;
            }
        }
        return idx;
    }

    value(position: number): any {
        return this._xScale.domain()[this.index(position)];
    }

    band(): number {
        return this._xScale.rangeBand();
    }

    domain(): any[] {
        return this._xScale.domain();
    }

    range(): any[] {
        return this._xScale.range();
    }

    render(data: CategoricalChartData[]): void {
        const band = (this._width || 0) / (Math.max(data.length, 1));
        d3.select('#' + this._clipPathId)
            .select('rect')
            .attr('transform', `translate(${-band / 2},0)`)
            .attr('width', Math.ceil(band));
        this._xScale
            .rangeBands([0, this._width], this._padding)
            .domain(data.map(d => d.id));
        this._xAxisGroup
            .attr('transform', `translate(${0},${this._height})`)
            .call(this._xAxis);
        const ticks = this._xAxisGroup.selectAll('.tick');
        ticks.select('text')
            .classed('tickLabel', true);
        ticks.style('clip-path', `url('#${this._clipPathId}')`)
            .data(data)
            .classed('clickable', d => !!d.clickCallback)
            .on('click', (d, i) => {
                d.clickCallback.bind(d);
                d.clickCallback();
            })
            .each(function (d, i) {
                const found = d;
                if (found) {
                    d3.select(this).attr(found.attributes);
                }
            })
            .select('text')
            .text(d => d.label);
    }
}